/**
 * Coordenadas de un punto para "Buscar por punto (lat/lng)": parseo y validación de lo que ingresa el usuario en
 * decimal o en grados, minutos y segundos (GMS), y conversión entre los dos formatos.
 *
 * Lógica pura: no depende de Google Maps ni de turf (por eso no importa `map-utils`), así se testea sin mocks.
 */

export enum CoordinateFormat {
    Decimal = 'decimal',
    Dms = 'dms'
}

export enum LatHemisphere {
    N = 'N',
    S = 'S'
}

export enum LngHemisphere {
    E = 'E',
    W = 'W'
}

export enum PointSearchError {
    /** Falta la latitud, la longitud o las dos. */
    Empty = 'empty',
    /** Decimal que no es un número (letras, dos separadores, etc.). */
    InvalidNumber = 'invalidNumber',
    LatOutOfRange = 'latOutOfRange',
    LngOutOfRange = 'lngOutOfRange',
    /** GMS mal formado: grados no enteros, minutos o segundos fuera de 0..59, hemisferio desconocido. */
    InvalidDms = 'invalidDms'
}

export interface DmsValue<H> {
    hemisphere: H;
    degrees: string | number;
    minutes: string | number;
    seconds: string | number;
}

export interface DecimalPointInput {
    lat: string | number;
    lng: string | number;
}

export interface DmsPointInput {
    lat: DmsValue<LatHemisphere>;
    lng: DmsValue<LngHemisphere>;
}

export interface LatLngPoint {
    lat: number;
    lng: number;
}

export type PointParseResult = { ok: true; point: LatLngPoint } | { ok: false; error: PointSearchError };

export const POINT_SEARCH_ERROR_MESSAGES: Record<PointSearchError, string> = {
    [PointSearchError.Empty]: 'Ingrese la latitud y la longitud',
    [PointSearchError.InvalidNumber]: 'Ingrese números válidos (por ejemplo -32.867622)',
    [PointSearchError.LatOutOfRange]: 'Latitud fuera de rango (entre -90 y 90)',
    [PointSearchError.LngOutOfRange]: 'Longitud fuera de rango (entre -180 y 180)',
    [PointSearchError.InvalidDms]: 'Grados enteros; minutos y segundos entre 0 y 59 (los segundos admiten decimales)'
};

/** Decimales del resultado (~0,1 m): el mismo nivel que muestra el indicador de centroide. */
const POINT_DECIMALS = 6;
/** Decimales de los segundos al pasar a GMS: 0,001" ≈ 3e-7°, por debajo del redondeo a 6 decimales. */
const SECONDS_DECIMALS = 3;

const DECIMAL_NUMBER = /^[+-]?(\d+([.,]\d*)?|[.,]\d+)$/;
const UNSIGNED_INTEGER = /^\d+$/;
const UNSIGNED_DECIMAL = /^(\d+([.,]\d*)?|[.,]\d+)$/;

const fail = (error: PointSearchError): PointParseResult => ({ ok: false, error });

const asText = (value: string | number | null | undefined): string =>
    value === null || value === undefined ? '' : String(value).trim();

/** Redondea a `decimals` sin devolver `-0`. */
const round = (value: number, decimals: number): number => {
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(value * factor) / factor;
    return rounded === 0 ? 0 : rounded;
};

/** Número con punto o coma decimal (`-32.86` o `-32,86`); `null` si no es un número. */
const toNumber = (text: string, pattern: RegExp): number | null => {
    if (!pattern.test(text)) return null;
    const value = Number(text.replace(',', '.'));
    return Number.isFinite(value) ? value : null;
};

/**
 * Separa un par "lat, lng" pegado en un solo campo. Devuelve los dos textos, o `null` si no es un par.
 *
 * Regla (la coma también es separador decimal, así que no alcanza con cortar por coma):
 * 1. `;` separa siempre: `-32,86;-57,39`.
 * 2. Coma seguida de espacio(s) separa: `-32.86, -57.39` y `-32,86, -57,39` (el formato que copia el centroide).
 * 3. Espacio(s) separan: `-32.86 -57.39` y `-32,86 -57,39`.
 * 4. Una sola coma sin espacio separa solo si los números usan punto decimal: `-32.86,-57.39`.
 * Cualquier otra cosa (por ejemplo `-32,86`) es un solo número con coma decimal.
 */
export const splitPastedLatLng = (value: string | number | null | undefined): [string, string] | null => {
    const text = asText(value);
    if (!text) return null;

    let parts: string[];
    if (text.includes(';')) {
        parts = text.split(';');
    } else if (/,\s+/.test(text)) {
        parts = text.split(/,\s+/);
    } else if (/\s/.test(text)) {
        parts = text.split(/\s+/);
    } else if (text.includes('.') && text.split(',').length === 2) {
        parts = text.split(',');
    } else {
        return null;
    }

    parts = parts.map(part => part.trim());
    if (parts.length !== 2 || parts.some(part => part === '')) return null;
    return [parts[0], parts[1]];
};

const checkRange = (point: LatLngPoint): PointParseResult => {
    if (Math.abs(point.lat) > 90) return fail(PointSearchError.LatOutOfRange);
    if (Math.abs(point.lng) > 180) return fail(PointSearchError.LngOutOfRange);
    return { ok: true, point: { lat: round(point.lat, POINT_DECIMALS), lng: round(point.lng, POINT_DECIMALS) } };
};

/**
 * Punto en decimal: latitud -90..90 y longitud -180..180, con punto o coma decimal. Si la latitud trae un par pegado
 * ("lat, lng", ver `splitPastedLatLng`) se usa el par y se ignora la longitud.
 */
export const parseDecimalPoint = (input: DecimalPointInput): PointParseResult => {
    let latText = asText(input.lat);
    let lngText = asText(input.lng);

    const pasted = splitPastedLatLng(latText);
    if (pasted) {
        [latText, lngText] = pasted;
    }

    if (!latText || !lngText) return fail(PointSearchError.Empty);

    const lat = toNumber(latText, DECIMAL_NUMBER);
    const lng = toNumber(lngText, DECIMAL_NUMBER);
    if (lat === null || lng === null) return fail(PointSearchError.InvalidNumber);

    return checkRange({ lat, lng });
};

const isEmptyDms = (value: DmsValue<unknown>): boolean =>
    !asText(value.degrees) && !asText(value.minutes) && !asText(value.seconds);

/** Valor absoluto en grados de un GMS, o `null` si está mal formado. Minutos y segundos vacíos valen 0. */
const dmsToAbsolute = (value: DmsValue<unknown>): number | null => {
    const degreesText = asText(value.degrees);
    const minutesText = asText(value.minutes) || '0';
    const secondsText = asText(value.seconds) || '0';

    if (!UNSIGNED_INTEGER.test(degreesText) || !UNSIGNED_INTEGER.test(minutesText)) return null;
    const degrees = Number(degreesText);
    const minutes = Number(minutesText);
    const seconds = toNumber(secondsText, UNSIGNED_DECIMAL);
    if (seconds === null || minutes >= 60 || seconds >= 60) return null;

    return degrees + minutes / 60 + seconds / 3600;
};

/**
 * Punto en GMS: grados enteros ≥ 0, minutos enteros 0..59, segundos 0 ≤ s < 60 (con punto o coma decimal); el total
 * no puede pasar de 90 (latitud) ni de 180 (longitud). S y W dan valores negativos.
 */
export const parseDmsPoint = (input: DmsPointInput): PointParseResult => {
    if (isEmptyDms(input.lat) || isEmptyDms(input.lng)) return fail(PointSearchError.Empty);

    const latHemisphereOk = input.lat.hemisphere === LatHemisphere.N || input.lat.hemisphere === LatHemisphere.S;
    const lngHemisphereOk = input.lng.hemisphere === LngHemisphere.E || input.lng.hemisphere === LngHemisphere.W;
    const latAbs = dmsToAbsolute(input.lat);
    const lngAbs = dmsToAbsolute(input.lng);
    if (!latHemisphereOk || !lngHemisphereOk || latAbs === null || lngAbs === null) {
        return fail(PointSearchError.InvalidDms);
    }

    return checkRange({
        lat: input.lat.hemisphere === LatHemisphere.S ? -latAbs : latAbs,
        lng: input.lng.hemisphere === LngHemisphere.W ? -lngAbs : lngAbs
    });
};

/** GMS de un valor decimal; los segundos se redondean a 3 decimales con acarreo (59,9996" → 1'). */
const toDms = <H>(decimal: number, positive: H, negative: H): DmsValue<H> => {
    const absolute = Math.abs(decimal);
    let degrees = Math.floor(absolute);
    let minutes = Math.floor((absolute - degrees) * 60);
    let seconds = round(((absolute - degrees) * 60 - minutes) * 60, SECONDS_DECIMALS);

    if (seconds >= 60) {
        seconds = 0;
        minutes += 1;
    }
    if (minutes >= 60) {
        minutes = 0;
        degrees += 1;
    }

    return { hemisphere: decimal < 0 ? negative : positive, degrees, minutes, seconds };
};

/** Punto decimal → GMS (para pasar lo cargado al cambiar el formato). Ida y vuelta da el mismo punto a 6 decimales. */
export const pointToDms = (point: LatLngPoint): DmsPointInput => ({
    lat: toDms(point.lat, LatHemisphere.N, LatHemisphere.S),
    lng: toDms(point.lng, LngHemisphere.E, LngHemisphere.W)
});
